/**
 * Typesense Predictive Search Component for Hollister Storefront
 * Queries Typesense Cloud directly & renders clean text suggestions with keyword highlight.
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener("mousedown", (event) => {
        // Prevent input from losing focus before click can register
        const clickable = event.target.closest("a, button, [role='option'], .predictive-search__item");
        if (clickable) {
          event.preventDefault();
        }
      });

      this.predictiveSearchResults.addEventListener("click", (event) => {
        const link = event.target.closest("a");
        if (link && link.href) {
          event.preventDefault();
          window.location.href = link.href;
          return;
        }

        const optionWithUrl = event.target.closest("[data-search-url]");
        if (optionWithUrl && optionWithUrl.dataset.searchUrl) {
          event.preventDefault();
          window.location.href = optionWithUrl.dataset.searchUrl;
          return;
        }

        const btn = event.target.closest(".predictive-search__item--term, button[type='submit'], [data-predictive-search-search-for-text]");
        if (btn) {
          event.preventDefault();
          const term = this.searchTerm || this.getQuery();
          if (term) {
            window.location.href = this.getSearchUrl(term);
          }
          return;
        }
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  getTypesenseConfig() {
    return {
      host: window.GTECH_TYPESENSE_HOST || "search-shopify.gtechdigital.id",
      port: Number(window.GTECH_TYPESENSE_PORT || 443),
      protocol: window.GTECH_TYPESENSE_PROTOCOL || "https",
      apiKey: window.GTECH_TYPESENSE_SEARCH_KEY || "KrLvgyLswrVrXy1OhvEFppyYCBfTLHvl",
      collection: window.GTECH_TYPESENSE_COLLECTION || "hollister_ph_product_colors",
      fallback: {
        host: window.GTECH_TYPESENSE_FALLBACK_HOST || "prevention-factors-oxford-drum.trycloudflare.com",
        port: Number(window.GTECH_TYPESENSE_FALLBACK_PORT || 443),
        protocol: window.GTECH_TYPESENSE_FALLBACK_PROTOCOL || "https",
        apiKey: window.GTECH_TYPESENSE_FALLBACK_SEARCH_KEY || "cA7AD1ep4ozdIaSQn9Ygn1RCIRsoqjiy",
        collection: window.GTECH_TYPESENSE_FALLBACK_COLLECTION || "hollister_ph_product_colors"
      }
    };
  }

  getSearchUrl(query) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const base = root.endsWith("/") ? root.slice(0, -1) : root;
    const searchPath = `${base}/search`;
    return `${searchPath}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
  }

  escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  highlightMatch(text, searchTerm) {
    if (!searchTerm || !text) return this.escapeHtml(text);
    const terms = searchTerm.trim().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return this.escapeHtml(text);
    const escapedTerms = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    const regex = new RegExp(`(${escapedTerms})`, "gi");

    const parts = text.split(regex);
    return parts
      .map((part) => {
        if (terms.some((t) => t.toLowerCase() === part.toLowerCase())) {
          return `<strong style="font-weight: 700; color: #1e2d3b;">${this.escapeHtml(part)}</strong>`;
        }
        return `<span style="font-weight: 400; color: #334155;">${this.escapeHtml(part)}</span>`;
      })
      .join("");
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    event.preventDefault();
    const query = this.getQuery();
    if (query.length > 0) {
      window.location.href = this.getSearchUrl(query);
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm?.length > 0) {
      document.querySelectorAll(".search-container-before").forEach((el) => {
        el.style.display = "none";
      });
    }

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else if (this.getAttribute("results") !== "false") {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        // Handled in onKeydown for immediate response
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    } else if (event.code === "Enter") {
      event.preventDefault();
      this.selectOption();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  adjustModalHeight() {
    const searchModal = this.closest(".search-modal");
    if (!searchModal) return;

    const resultsContainer = this.querySelector("#predictive-search-results");
    if (resultsContainer && this.getAttribute("open") === "true") {
      const resultsRect = resultsContainer.getBoundingClientRect();
      const modalRect = searchModal.getBoundingClientRect();
      const calculatedHeight = Math.ceil(resultsRect.bottom - modalRect.top + 50);
      const finalHeight = Math.max(calculatedHeight, 520);
      searchModal.style.setProperty("min-height", `${finalHeight}px`, "important");
      searchModal.style.setProperty("height", `${finalHeight}px`, "important");
    } else {
      searchModal.style.removeProperty("height");
      searchModal.style.removeProperty("min-height");
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item, .predictive-search__list-item a")
    ).filter((element) => element.offsetParent !== null);

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = "";

    let activeElementIndex = 0;
    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"], [aria-selected="true"]'
    );

    if (selectedOption) {
      const link = selectedOption.tagName === "A" ? selectedOption : selectedOption.querySelector("a");
      if (link && link.href) {
        window.location.href = link.href;
        return;
      }
      if (selectedOption.dataset && selectedOption.dataset.searchUrl) {
        window.location.href = selectedOption.dataset.searchUrl;
        return;
      }
      selectedOption.click();
    } else {
      const query = this.getQuery();
      if (query.length > 0) {
        window.location.href = this.getSearchUrl(query);
      }
    }
  }

  /**
   * Fetch suggestions from Typesense
   */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const config = this.getTypesenseConfig();
    const portPart = config.port && config.port !== 443 && config.port !== 80 ? `:${config.port}` : "";
    const params = new URLSearchParams({
      q: searchTerm,
      query_by: "title,color_label,base_color,tags,product_type,vendor",
      sort_by: "inventory_available:desc,_text_match:desc",
      per_page: "30",
      prefix: "true"
    });

    const searchUrl = `${config.protocol}://${config.host}${portPart}/collections/${config.collection}/documents/search?${params.toString()}`;

    const executeFetch = (url, apiKey, isFallback) => {
      return fetch(url, {
        headers: {
          "X-TYPESENSE-API-KEY": apiKey
        },
        signal: this.abortController.signal
      }).then((response) => {
        if (!response.ok) {
          throw new Error("HTTP " + response.status);
        }
        return response.json();
      }).catch((err) => {
        if (!isFallback && config.fallback && config.fallback.host && config.fallback.apiKey) {
          console.warn("⚠️ [Predictive Search] Primary search node failed, trying fallback...", err);
          const fbPort = config.fallback.port && config.fallback.port !== 443 && config.fallback.port !== 80 ? `:${config.fallback.port}` : "";
          const fbUrl = `${config.fallback.protocol}://${config.fallback.host}${fbPort}/collections/${config.fallback.collection}/documents/search?${params.toString()}`;
          return executeFetch(fbUrl, config.fallback.apiKey, true);
        }
        throw err;
      });
    };

    executeFetch(searchUrl, config.apiKey, false)
      .then((data) => {
        const resultsMarkup = this.buildTypesenseMarkup(searchTerm, data);
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === "AbortError" || error?.code === 20) {
          return;
        }
        console.error("⚠️ [Predictive Search Typesense Error]:", error);
        this.close();
      });
  }

  /**
   * Builds the predictive search HTML markup with clean suggestions list & pill button
   */
  buildTypesenseMarkup(searchTerm, data) {
    const hits = data?.hits || [];
    const seenTitles = new Set();
    const uniqueTitles = [];

    for (const h of hits) {
      const doc = h?.document;
      if (!doc || !doc.title) continue;
      const rawTitle = doc.title.trim();
      const lower = rawTitle.toLowerCase();
      if (!seenTitles.has(lower)) {
        seenTitles.add(lower);
        uniqueTitles.push(rawTitle);
        if (uniqueTitles.length >= 10) break;
      }
    }

    const hasSuggestions = uniqueTitles.length > 0;
    let html = '<div id="predictive-search-results" role="listbox" style="padding: 1.5rem 0.5rem 1rem 0.5rem;">';

    if (hasSuggestions) {
      html += `
        <div
          id="predictive-search-results-groups-wrapper"
          class="predictive-search__results-groups-wrapper"
          style="border: none; display: block;"
        >
          <div class="predictive-search__result-group" style="display: block;">
            <h2
              id="predictive-search-queries"
              class="predictive-search__heading"
              style="font-size: 1.5rem; font-weight: 700; color: #233b4d; border: none; margin: 0 0 1.2rem 0; padding: 0; letter-spacing: normal; text-transform: none;"
            >
              Suggestions
            </h2>
            <ul
              id="predictive-search-results-queries-list"
              class="predictive-search__results-list list-unstyled"
              role="group"
              aria-labelledby="predictive-search-queries"
              style="display: flex; flex-direction: column; gap: 0.7rem; margin: 0; padding: 0;"
            >
      `;

      uniqueTitles.forEach((title, index) => {
        const queryUrl = this.getSearchUrl(title);
        const lowerTitle = title.toLowerCase();

        html += `
          <li
            id="predictive-search-option-query-${index + 1}"
            class="predictive-search__list-item"
            role="option"
            aria-selected="false"
            data-search-url="${this.escapeHtml(queryUrl)}"
            style="padding: 0;"
          >
            <a
              href="${this.escapeHtml(queryUrl)}"
              class="predictive-search__item link link--text"
              tabindex="-1"
              style="padding: 0.45rem 0.5rem; color: #233b4d; text-decoration: none; display: block; width: 100%; font-size: 1.45rem; line-height: 1.4; border-radius: 4px; cursor: pointer;"
            >
              <p
                class="predictive-search__item-heading"
                style="margin: 0; font-size: 1.45rem; line-height: 1.4; text-transform: lowercase; pointer-events: none;"
              >
                ${this.highlightMatch(lowerTitle, searchTerm)}
              </p>
            </a>
          </li>
        `;
      });

      html += `
            </ul>
          </div>
        </div>
      `;
    }

    // See all results pill button
    html += `
      <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button" style="margin-top: 1.8rem; margin-bottom: 1.2rem; border: none; padding: 0;">
        <button
          class="predictive-search__item predictive-search__item--term"
          tabindex="-1"
          role="option"
          aria-selected="false"
          type="button"
          data-search-url="${this.escapeHtml(this.getSearchUrl(searchTerm))}"
          style="background-color: #233b4d; color: #ffffff; border-radius: 9999px; padding: 1.1rem 2.4rem; font-size: 1.4rem; font-weight: 700; border: none; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; width: auto; text-decoration: none; transition: background-color 0.2s;"
        >
          <span data-predictive-search-search-for-text style="color: #ffffff; font-weight: 700; pointer-events: none;">
            See all results for “${this.escapeHtml(searchTerm)}”
          </span>
        </button>
      </div>
    `;

    html += `
      <span class="hidden" data-predictive-search-live-region-count-value>
        ${uniqueTitles.length} suggestions found
      </span>
    </div>`;

    return html;
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text") || "Loading...";

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    const hasResults = this.predictiveSearchResults.querySelector(
      "#predictive-search-results-groups-wrapper"
    );

    if (!hasResults) {
      this.removeAttribute("loading");
      this.setAttribute("results", false);
      this.close();
      return;
    }

    this.setAttribute("results", true);

    document.querySelectorAll(".search-container-before").forEach((el) => {
      el.style.display = "none";
    });

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countEl = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countEl) {
      this.setLiveRegionText(countEl.textContent);
    }
  }

  getResultsMaxHeight() {
    const headerBottom = document.querySelector(".section-header")?.getBoundingClientRect().bottom || 0;
    this.resultsMaxHeight = window.innerHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    requestAnimationFrame(() => {
      this.adjustModalHeight();
    });

    this.dispatchEvent(new CustomEvent("predictive-search:open", { bubbles: true }));
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    document.querySelectorAll(".search-container-before").forEach((el) => {
      el.style.display = "block";
    });

    this.dispatchEvent(new CustomEvent("predictive-search:close", { bubbles: true }));
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    const searchModal = this.closest(".search-modal");
    if (searchModal) {
      searchModal.style.removeProperty("height");
      searchModal.style.removeProperty("min-height");
    }

    document.querySelectorAll(".search-container-before").forEach((el) => {
      el.style.display = "block";
    });
  }
}

customElements.define("predictive-search", PredictiveSearch);
